import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, ButtonGroup } from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { useRef, useState, useEffect } from 'react';
import type { ButtonGroupProps } from '@mui/material';
import type { FC } from 'react';

interface SplitButtonProps
  extends Pick<ButtonGroupProps, 'color' | 'variant' | 'size' | 'sx'> {
  options: Array<{ label: string; action: VoidFunction }>;
  id: string;
}

const SplitButton: FC<SplitButtonProps> = ({ options, id, ...params }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const storageKey = `splitButtonSelectedIndex-${id}`;
  const [selectedIndex, setSelectedIndex] = useState<number>(() => {
    try {
      const savedIndex = sessionStorage.getItem(storageKey);
      const index = savedIndex !== null ? parseInt(savedIndex, 10) : 0;
      return options.length <= index ? 0 : index;
    } catch (error) {
      console.error('Could not access sessionStorage:', error);
      return 0;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, selectedIndex.toString());
    } catch (error) {
      console.error('Could not save to sessionStorage:', error);
    }
  }, [selectedIndex, storageKey]);

  const handleMenuItemClick = (index: number): void => {
    setSelectedIndex(index);
    setOpen(false);
  };

  const handleToggle = (): void => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent): void => {
    if (anchorRef.current?.contains(event.target as HTMLElement) ?? false) {
      return;
    }
    setOpen(false);
  };

  return (
    <>
      <ButtonGroup {...params} ref={anchorRef} aria-label='split button'>
        <Button onClick={options[selectedIndex]?.action}>
          {options[selectedIndex]?.label}
        </Button>
        <Button
          size='small'
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label='select merge strategy'
          aria-haspopup='menu'
          onClick={handleToggle}>
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{ zIndex: 1 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id='split-button-menu' autoFocusItem>
                  {options.map((option, index) => (
                    <MenuItem
                      key={option.label}
                      selected={index === selectedIndex}
                      onClick={() => {
                        handleMenuItemClick(index);
                      }}>
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default SplitButton;
